import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Send, Lock, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";

const RELATIONSHIPS = ["Spouse / Partner", "Parent", "Sibling", "Child", "Extended family", "Close friend", "Attorney", "Other"];
const PRIOR_ACTIONS = [
  { k: "lawsuit", label: "Lawsuit filed" },
  { k: "grievance", label: "Grievance filed with facility" },
  { k: "press", label: "Press / media coverage" },
  { k: "none", label: "No formal action yet" },
];
const HELP_OPTIONS = [
  { k: "feature", label: "Feature them in 'Still Inside' on the site" },
  { k: "journalists", label: "Connect us with journalists" },
  { k: "legal", label: "Legal referral or resources" },
  { k: "letters", label: "Organize a letter-writing campaign" },
  { k: "record", label: "Just want you to know — for the record" },
];

const EMPTY = {
  loved_one_name: "", loved_one_age: "", facility: "", time_in_solitary: "",
  mental_health_history: "", current_condition: "", prior_actions: [], help_requested: [],
  submitter_name: "", relationship: "", submitter_email: "", submitter_phone: "", photo: "",
  consent_permission: false, consent_followup: false, consent_public: false, anonymous: false,
};

export default function TellTheirStoryPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = "Tell Us Their Story · IMI"; window.scrollTo(0, 0); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const canProceedStep = {
    1: !!form.loved_one_name.trim(),
    2: true, // optional details
    3: true,
    4: !!form.submitter_email.trim() && form.consent_permission,
  };

  const submit = async () => {
    if (!form.submitter_email.trim()) { toast.error("Email is required so we can respond."); return; }
    if (!form.consent_permission) { toast.error("Please confirm you have permission to share this story."); return; }
    setLoading(true);
    try {
      await api.post("/family-story", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-imi-bg text-imi-text">
      <Header />
      <main className="flex-1 pt-32 md:pt-36 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Reveal>
            <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-imi-text/50 hover:text-imi-gold text-[11px] uppercase tracking-[0.25em] mb-8">
              <ArrowLeft size={14} /> Back to home
            </Link>
            <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-6">Someone you love is still inside?</p>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
              Tell Us <span className="italic gold-gradient-text">Their Story</span>.
            </h1>
            <p className="mt-6 text-imi-text/70 text-lg leading-relaxed max-w-2xl">
              Nobody should be forgotten. Share what you know — a name, a facility, a story — and help us bring their voice out of isolation.
            </p>

            {/* Trust signal */}
            <div className="mt-6 border border-imi-gold/30 bg-imi-gold/5 p-4 flex items-start gap-3">
              <Lock size={18} className="text-imi-gold mt-0.5 shrink-0" />
              <p className="text-imi-text/70 text-sm leading-relaxed">
                <span className="text-imi-goldlight font-semibold">Your submission is confidential.</span> Nothing appears publicly until you approve it. Anonymous submissions are welcome — your loved one's name will never be published without additional verification.
              </p>
            </div>
          </Reveal>

          {done ? (
            <Reveal className="mt-12">
              <div className="border border-imi-gold/40 bg-imi-elevated p-10 md:p-14 text-center" data-testid="family-story-thanks">
                <Sparkles size={48} className="text-imi-gold mx-auto mb-6" />
                <h2 className="font-serif text-3xl md:text-4xl text-imi-goldlight mb-4">Story received.</h2>
                <p className="text-imi-text/70 text-base md:text-lg leading-relaxed max-w-lg mx-auto">
                  Thank you for trusting IMI with {form.loved_one_name || "your loved one"}'s story. A member of our team will be in touch within <span className="text-imi-goldlight font-semibold">48 hours</span>. A confirmation email is on its way to <span className="text-imi-gold">{form.submitter_email}</span>.
                </p>
                <div className="mt-8 pt-8 border-t border-imi-border text-imi-text/50 text-sm">
                  If your loved one is in immediate crisis, please also contact your state's Prison Legal News hotline or a Disability Rights advocate.
                </div>
                <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-imi-gold text-imi-bg px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-imi-goldlight transition-colors">
                  Back to IMI
                </Link>
              </div>
            </Reveal>
          ) : (
            <>
              {/* Step indicator */}
              <div className="mt-12 mb-10 flex items-center gap-3">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="flex-1 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${step >= n ? "bg-imi-gold text-imi-bg" : "bg-imi-elevated text-imi-text/40 border border-imi-border"}`} data-testid={`step-indicator-${n}`}>
                      {step > n ? <CheckCircle2 size={16} /> : n}
                    </div>
                    {n < 4 && <div className={`flex-1 h-px ${step > n ? "bg-imi-gold" : "bg-imi-border"}`} />}
                  </div>
                ))}
              </div>

              <form onSubmit={(e) => { e.preventDefault(); step === 4 ? submit() : setStep(step + 1); }} className="border border-imi-border bg-imi-surface/40 p-6 md:p-10 space-y-6">
                <AnimatePresence mode="wait">
                  <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                    {step === 1 && (
                      <div className="space-y-5" data-testid="step-1">
                        <div className="text-[11px] uppercase tracking-[0.25em] text-imi-gold">Step 1 · About your loved one</div>
                        <TextField label="Their full name *" value={form.loved_one_name} onChange={(v) => set("loved_one_name", v)} testid="loved-one-name" required />
                        <div className="grid sm:grid-cols-2 gap-4">
                          <TextField label="Their approximate age" value={form.loved_one_age} onChange={(v) => set("loved_one_age", v)} testid="loved-one-age" placeholder="e.g. 42" />
                          <TextField label="Facility name (if known)" value={form.facility} onChange={(v) => set("facility", v)} testid="facility" placeholder="e.g. ADX Florence" />
                        </div>
                        <TextField label="How long have they been in solitary / isolation?" value={form.time_in_solitary} onChange={(v) => set("time_in_solitary", v)} testid="time-solitary" placeholder="e.g. Since March 2019 · Approximately 6 years · Not sure" />
                      </div>
                    )}
                    {step === 2 && (
                      <div className="space-y-5" data-testid="step-2">
                        <div className="text-[11px] uppercase tracking-[0.25em] text-imi-gold">Step 2 · Their situation</div>
                        <TextAreaField label="Mental health history (optional)" value={form.mental_health_history} onChange={(v) => set("mental_health_history", v)} testid="mh-history" placeholder="Diagnoses, medications, prior hospitalizations — anything you feel comfortable sharing." />
                        <TextAreaField label="Their current condition (optional)" value={form.current_condition} onChange={(v) => set("current_condition", v)} testid="current-condition" placeholder="What you're hearing in letters, phone calls, or from other family members." />
                        <div>
                          <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-3">Have any of these already happened?</label>
                          <div className="grid sm:grid-cols-2 gap-2">
                            {PRIOR_ACTIONS.map((o) => (
                              <CheckboxItem key={o.k} label={o.label} checked={form.prior_actions.includes(o.k)} onChange={() => toggleArr("prior_actions", o.k)} testid={`prior-${o.k}`} />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {step === 3 && (
                      <div className="space-y-5" data-testid="step-3">
                        <div className="text-[11px] uppercase tracking-[0.25em] text-imi-gold">Step 3 · How can IMI help?</div>
                        <p className="text-imi-text/50 text-sm">Pick anything that would be meaningful. We may not be able to do all of it — but we'll be honest about what we can.</p>
                        <div className="space-y-2">
                          {HELP_OPTIONS.map((o) => (
                            <CheckboxItem key={o.k} label={o.label} checked={form.help_requested.includes(o.k)} onChange={() => toggleArr("help_requested", o.k)} testid={`help-${o.k}`} />
                          ))}
                        </div>
                      </div>
                    )}
                    {step === 4 && (
                      <div className="space-y-5" data-testid="step-4">
                        <div className="text-[11px] uppercase tracking-[0.25em] text-imi-gold">Step 4 · About you</div>
                        <CheckboxItem
                          label="I want to submit this anonymously (my name kept private)"
                          checked={form.anonymous}
                          onChange={() => set("anonymous", !form.anonymous)}
                          testid="anonymous-toggle"
                          highlight
                        />
                        <TextField
                          label={form.anonymous ? "Codename or alias (e.g. 'Marcus's sister')" : "Your name"}
                          value={form.submitter_name}
                          onChange={(v) => set("submitter_name", v)}
                          testid="submitter-name"
                        />
                        <div>
                          <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Your relationship</label>
                          <select value={form.relationship} onChange={(e) => set("relationship", e.target.value)} data-testid="relationship" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold">
                            <option value="">— select —</option>
                            {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <TextField label="Your email *" value={form.submitter_email} onChange={(v) => set("submitter_email", v)} testid="submitter-email" type="email" required placeholder="you@example.com" />
                          <TextField label="Phone (optional)" value={form.submitter_phone} onChange={(v) => set("submitter_phone", v)} testid="submitter-phone" placeholder="For urgent follow-up only" />
                        </div>

                        <div className="pt-4 border-t border-imi-border space-y-3">
                          <div className="text-[11px] uppercase tracking-[0.2em] text-imi-gold mb-2">Consent</div>
                          <CheckboxItem label="I have permission from my loved one (or their legal guardian) to share this story with IMI." checked={form.consent_permission} onChange={() => set("consent_permission", !form.consent_permission)} testid="consent-permission" required />
                          <CheckboxItem label="IMI may contact me for follow-up." checked={form.consent_followup} onChange={() => set("consent_followup", !form.consent_followup)} testid="consent-followup" />
                          <CheckboxItem label="IMI may publicly feature this story (in Still Inside, articles, films). — Can be revoked anytime." checked={form.consent_public} onChange={() => set("consent_public", !form.consent_public)} testid="consent-public" />
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Navigation */}
                <div className="pt-4 border-t border-imi-border flex items-center justify-between gap-3">
                  {step > 1 ? (
                    <button type="button" onClick={() => setStep(step - 1)} data-testid="prev-step" className="inline-flex items-center gap-2 text-imi-text/60 hover:text-imi-goldlight px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors">
                      <ArrowLeft size={14} /> Back
                    </button>
                  ) : <span />}
                  <button
                    type="submit"
                    disabled={loading || !canProceedStep[step]}
                    data-testid={step === 4 ? "submit-story" : "next-step"}
                    className="inline-flex items-center gap-2 bg-imi-gold text-imi-bg px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-imi-goldlight disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? "Sending…" : step === 4 ? (<>Submit story <Send size={14} /></>) : (<>Continue <ArrowRight size={14} /></>)}
                  </button>
                </div>
              </form>

              <p className="mt-6 text-imi-text/40 text-xs text-center">
                Communications with IMI are protected under standard journalist-source protections. If your loved one is in immediate crisis, also contact Prison Legal News or a Disability Rights advocate in your state.
              </p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

const TextField = ({ label, value, onChange, testid, placeholder, type = "text", required }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      data-testid={testid}
      className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text placeholder:text-imi-text/40 outline-none focus:border-imi-gold"
    />
  </div>
);

const TextAreaField = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">{label}</label>
    <textarea
      rows={4}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text placeholder:text-imi-text/40 outline-none focus:border-imi-gold resize-y"
    />
  </div>
);

const CheckboxItem = ({ label, checked, onChange, testid, highlight, required }) => (
  <label className={`flex items-start gap-3 cursor-pointer text-sm p-3 border transition-colors ${checked ? "border-imi-gold/40 bg-imi-gold/5" : "border-imi-border hover:border-imi-gold/30"} ${highlight ? "border-imi-gold/50" : ""}`}>
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="mt-0.5 accent-imi-gold" />
    <span className="text-imi-text/80 leading-relaxed">{label}{required && <span className="text-imi-gold"> *</span>}</span>
  </label>
);
